import React from 'react';
import { Box, Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Category as CategoryType, Component as ComponentType } from '@Types';
import { useGlobalization } from '@Translations/useGlobalization';

interface CategoryFooterProps {
	category?: CategoryType;
	components?: ComponentType[];
	forLoF: boolean;
	isLoading: boolean;
	canBeDiscarded: boolean;
	onAddComponent?: (
		forLoF: boolean,
		category: CategoryType | undefined,
	) => void;
	onDiscardClick: () => void;
}

export const CategoryFooter = ({
	category,
	components,
	forLoF,
	isLoading,
	canBeDiscarded,
	onAddComponent,
	onDiscardClick,
}: CategoryFooterProps) => {
	const { t } = useGlobalization();

	// Discard must stay reachable even on an empty category (it's the only
	// way out of one created by mistake) — only the Add Component button is
	// skipped when there are no components yet, since the empty state
	// already offers its own "Add Component" call to action.
	if (isLoading) return null;
	const showAddComponent = (components?.length ?? 0) > 0;
	const showDiscard = canBeDiscarded && !category?.isCondition;
	if (!showAddComponent && !showDiscard) return null;

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				mt: 1,
			}}>
			{showAddComponent ? (
				<Button
					variant="text"
					color="primary"
					startIcon={<Add />}
					onClick={() => onAddComponent?.(forLoF, category)}>
					{t('Add Component')}
				</Button>
			) : (
				<span />
			)}
			{showDiscard && (
				<Button
					variant="text"
					onClick={onDiscardClick}
					sx={{ color: 'text.secondary' }}>
					{t('Discard category')}
				</Button>
			)}
		</Box>
	);
};
